import React from 'react';
import HLCard from 'components/HLCard';
import { Box, Grid, Chip, Select, MenuItem, CardContent, CardHeader, Typography } from '@mui/material';
import { TimeFrameOptions } from 'pages/Dashboard/utils/trackingUtils';
import HLButton from 'components/HLButton';
import LoadingGif from 'assets/Loading.gif';
import { ReactComponent as NoDataFromLogsSvg } from 'assets/NoDataFromLogs.svg';
import { ReactComponent as NoDataFromDeviceSvg } from 'assets/NoDataFromDevice.svg';
import { border, tertiary } from 'theme';


const noDataContentMap = {
  default: {
    icon: NoDataFromLogsSvg,
    title: 'No activity during this time period.',
    description: 'Try selecting a different time-frame or data-source. You can nudge your patient during their next appointment to log in Headlamp.',
  },
  device: {
    icon: NoDataFromDeviceSvg,
    title: 'This patient hasn’t connected their device.',
    description: 'They can access permissions by going to their profile in their Headlamp app.',
  },
};

export function NoDataState({ type = 'default' }: { type?: 'default' | 'device' }) {
  const { icon: Icon, title, description } = noDataContentMap[type];

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      sx={{ flex: 1, px: 3 }}
    >
      <Icon />
      <Box>
        <Typography variant='body1' fontWeight={600} color='text.primary' textAlign='center' mt={4} mb={1}>
          {title}
        </Typography>
        <Typography variant='body2' color='text.secondary' textAlign='center'>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export function LoadingState() {
  return (
    <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' sx={{ flex: 1 }}>
      <img src={LoadingGif} alt='Loading...' width={300} height={74} />
    </Box>
  );
}

export interface PatientDetailsCardProps {
  noData?: boolean;
  isLoading?: boolean;
  selectTimeFrame?: string;
  title: string;
  onTimeFrameChange?: (value: string) => void;
  avgChipLabel?: string;
  showViewDetails?: boolean;
  onViewDetails?: () => void;
  gridProps?: Partial<React.ComponentProps<typeof Grid>>;
  children?: React.ReactNode;
  infoIconTooltip?: React.ReactElement; // <-- Added prop
  showInfoIcon?: boolean; // <-- Added prop
  showHeader?: boolean; // <-- Added prop
  viewDetailsBtnLabel?: string;
  showSubHeader?: boolean;
  renderAction?: React.ReactNode;
  numberOfLogs?: number;
  noDataType?: 'default' | 'device';
}

function PatientDetailsCard({
  selectTimeFrame,
  title,
  avgChipLabel,
  showViewDetails = false,
  onViewDetails,
  gridProps,
  children,
  infoIconTooltip,
  showInfoIcon = false,
  showHeader = true,
  viewDetailsBtnLabel = 'View Details',
  showSubHeader = true,
  renderAction = null,
  onTimeFrameChange,
  noData = false,
  isLoading = false,
  numberOfLogs,
  noDataType,
}: PatientDetailsCardProps) {
  const timeFrameValues = React.useMemo(() => Object.values(TimeFrameOptions), []);
  const selectedTimeFrame = React.useMemo(
    () => timeFrameValues.findIndex((value) => value === selectTimeFrame),
    [selectTimeFrame, timeFrameValues],
  );
  const timeFrameOptions = React.useMemo(
    () => timeFrameValues.map((val, idx) => ({ label: val, value: idx })),
    [timeFrameValues],
  );

  const handleTabsValueChange = (value: number | string) => {
    const tfValue = timeFrameValues[value as number];
    onTimeFrameChange?.(tfValue);
  };

  return (
    <Grid item {...gridProps}>
      <HLCard sx={{ minHeight: 448, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {showHeader && (
          <CardHeader
            title={(
              <Box display='flex' alignItems='center'>
                {title}
                {showInfoIcon && infoIconTooltip}
              </Box>
            )}
            titleTypographyProps={{
              variant: 'body1',
              fontWeight: 600,
              color: 'text.primary',
            }}
            subheader={
              showSubHeader && (
                <Select
                  value={selectedTimeFrame}
                  onChange={(e) => handleTabsValueChange(e.target.value as string | number)}
                  variant='standard'
                  sx={(theme) => ({
                    ...theme.typography.caption,
                    '.MuiSelect-select': {
                      color: 'text.secondary',
                      padding: 0,
                      ...theme.typography.caption,
                    },
                    '.MuiSvgIcon-root': {
                      color: 'text.secondary',
                      fontSize: 16,
                    },
                    '& .MuiSelect-select:focus': {
                      backgroundColor: 'transparent',
                    },
                    '&:before, &:after': {
                      display: 'none',
                    },
                  })}
                >
                  {timeFrameOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={(theme) => ({
                        ...theme.typography.caption,
                        color: 'text.secondary',
                      })}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )
            }
            sx={{
              p: 1.5,
              pb: 1,
              '& .MuiCardHeader-action': {
                margin: 0,
              },
            }}
            action={(
              <Box height={38}>
                {renderAction ?? (showViewDetails ? (
                  <HLButton variant='text' size='small' onClick={onViewDetails}>
                    {viewDetailsBtnLabel}
                  </HLButton>
                ) : null)}
              </Box>
            )}
          />
        )}
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            py: 0,
            px: 1.5,
            flex: 1,
            ':last-child': { pb: 1.5 },
          }}
        >
          {isLoading && (
            <LoadingState />
          )}
          {noData && !isLoading && (
            <NoDataState type={noDataType} />
          )}
          {!isLoading && !noData && (avgChipLabel || numberOfLogs !== undefined) && (
            <Box display='flex' flexDirection='row' alignItems='center' gap={2}>
              {avgChipLabel && (
              <Chip
                label={avgChipLabel}
                size='small'
                sx={(theme) => ({
                  alignSelf: 'flex-start',
                  mt: 1.5,
                  mb: 2.5,
                  ...theme.typography.caption,
                  background: tertiary.light,
                  color: tertiary.dark,
                })}
              />
              )}
              {numberOfLogs !== undefined && (
              <Chip
                label={`${numberOfLogs} ${numberOfLogs === 1 ? 'log' : 'logs'} in past ${selectTimeFrame}`}
                size='small'
                sx={(theme) => ({
                  alignSelf: 'flex-start',
                  mt: 1.5,
                  mb: 2.5,
                  ...theme.typography.caption,
                  background: border.third,
                  color: 'text.secondary',
                })}
              />
              )}
            </Box>
          )}
          {!isLoading && !noData && children}
        </CardContent>
      </HLCard>
    </Grid>
  );
}

export default PatientDetailsCard;
